<template>
  <div
    class="switch-button-holder"
    v-b-tooltip="{ text: toggleTooltip ? toggleTooltip : '' }"
    :class="[size, { disabled: isDisabled }, classes]"
  >
    <label class="toggle-wrapper">
      <input
        class="toggle-input"
        type="checkbox"
        role="checkbox"
        tabindex="0"
        :data-qa="dataQa"
        :class="{ 'input-active': active }"
        :checked="buttonValue"
        :aria-checked="buttonValue"
        :disabled="isDisabled"
        @change="handleChange($event)"
        @keydown.space="setActive(true)"
        @keyup.space="setActive(false)"
        @keydown.enter="setActive(true)"
        @keyup.enter="
          ($event.target as HTMLInputElement).checked = !($event.target as HTMLInputElement)
            .checked;
          handleChange($event);
          setActive(false);
        "
      />
      <span
        class="slider"
        @mousedown="setActive(true)"
        @mouseleave="setActive(false)"
        @mouseup="setActive(false)"
      />
    </label>
    <span v-if="label" class="label">
      {{ label }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

export type Size = 'big' | 'small';

interface FormToggleProps {
  buttonValue?: boolean;
  classes?: string;
  label?: string;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  size?: Size;
  dataQa?: string;
  toggleTooltip?: string;
}

const props = withDefaults(defineProps<FormToggleProps>(), {
  buttonValue: false,
  classes: '',
  label: '',
  disabled: false,
  size: 'big',
  dataQa: '',
});

interface FormToggleEmits {
  (e: 'update:buttonValue', value: boolean): void;
}

const emit = defineEmits<FormToggleEmits>();

const isDisabled = computed(() => {
  return props.disabled || (props.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const handleChange = (event: Event) => {
  emit('update:buttonValue', (event.target as HTMLInputElement).checked);
  clearFocus(event);
};

const clearFocus = (event: Event) => {
  if (event.pointerType === 'mouse') {
    (event.currentTarget as HTMLElement)?.blur();
  }
};

const active = ref(false);

const setActive = (state: boolean) => {
  active.value = state;
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.switch-button-holder {
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 24px;
  padding: 0;
}

.toggle-wrapper {
  display: inline-block;
  position: relative;
}

/* Slider */

.slider {
  background-color: map.get(colors.$colors, 'dusty-gray');
  border-radius: 12px;
  cursor: pointer;
  height: 100%;
  position: absolute;
  width: 100%;

  &::before {
    background-color: map.get(colors.$colors, 'silver');
    border-radius: 50%;
    bottom: 3px;
    content: '';
    height: 18px;
    left: 3px;
    position: absolute;
    top: 3px;
    transition: transform 0.4s ease;
    width: 18px;
  }
}

.toggle-input {
  height: 0;
  margin: 0;
  opacity: 0;
  width: 0;

  &:focus:not(:active) + .slider {
    outline: 4px solid map.get(colors.$colors, 'magic-mint');
  }

  &:checked + .slider {
    background-color: map.get(colors.$colors, 'jewel');
  }

  &:not(:checked) + .slider:hover {
    background-color: map.get(colors.$colors, 'silver');
  }

  &:checked + .slider:hover {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
  }

  &:not(:checked).input-active + .slider {
    background-color: map.get(colors.$colors, 'shuttle-gray');
    outline: 0;
  }

  &:checked.input-active + .slider {
    background-color: map.get(colors.$colors, 'stratos');
    outline: 0;
  }

  &:checked + .slider::before {
    background-color: map.get(colors.$colors, 'white');
    transform: translateX(24px);
  }

  &:not(:checked) + .slider:hover::before {
    background-color: map.get(colors.$colors, 'gallery');
  }
}

.label {
  color: map.get(colors.$colors, 'cod-gray');
  display: inline-block;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  position: relative;
}

.switch-button-holder.disabled {
  pointer-events: none;

  .toggle-input + .slider {
    opacity: 0.3;
  }

  .label {
    color: map.get(colors.$colors, 'silver');
  }
}

.switch-button-holder.big {
  gap: 8px;

  .toggle-wrapper {
    height: 24px;
    width: 48px;
  }
}

.switch-button-holder.small {
  gap: 6px;

  .toggle-wrapper {
    height: 16px;
    width: 32px;
  }

  .toggle-input + .slider::before {
    height: 10px;
    width: 10px;
  }

  .toggle-input:checked + .slider::before {
    transform: translateX(16px);
  }
}
</style>
